import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CurrencyIcon } from '@/components/profile/CurrencyIcon'
import { formatBalance } from '@/lib/format-number'
import { cn } from '@/lib/utils'
import { CHICKEN_DIFFICULTIES } from '@/config/original'
import { useGetCurrenciesQuery } from '@/store/api/paymentApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import {
	selectChickenGame,
	selectChickenStepMultiplier,
	setChickenAutoBetSettings,
	setChickenBetAmount,
	setChickenBetMode,
	setChickenDifficulty,
} from '@/store/slices/chickenSlice'
import type { ChickenBetMode, ChickenDifficulty } from '@/types/chicken'
import type { GameController } from '@/components/original/games/chicken/core/GameController'

interface ChickenBetPanelProps {
	controller: GameController | null
	className?: string
}

const DIFFICULTY_LABELS: Record<ChickenDifficulty, string> = {
	easy: 'Easy',
	medium: 'Medium',
	hard: 'Hard',
	hardcore: 'Hardcore',
}

export function ChickenBetPanel({ controller, className }: ChickenBetPanelProps) {
	const dispatch = useAppDispatch()
	const chicken = useAppSelector(selectChickenGame)
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const { data: currenciesResponse } = useGetCurrenciesQuery()
	const [amountDraft, setAmountDraft] = useState(chicken.betAmount)
	const [autoBetCountDraft, setAutoBetCountDraft] = useState(String(chicken.autoBetSettings.betCount))
	const [cashoutStepsDraft, setCashoutStepsDraft] = useState(String(chicken.autoBetSettings.steps))
	const [stopOnProfitDraft, setStopOnProfitDraft] = useState(String(chicken.autoBetSettings.stopOnProfit))
	const [stopOnLossDraft, setStopOnLossDraft] = useState(String(chicken.autoBetSettings.stopOnLoss))

	const currencies = currenciesResponse?.data ?? []
	const currency = useMemo(() => currencies.find((item) => item.currency_code.toUpperCase() === chicken.currencyCode.toUpperCase()), [currencies, chicken.currencyCode])

	const usdAmount = useMemo(() => {
		const amount = Number(amountDraft)
		const rate = Number(currency?.exchange_rate ?? 0)

		if (!Number.isFinite(amount) || !Number.isFinite(rate)) {
			return 0
		}

		return amount * rate
	}, [amountDraft, currency?.exchange_rate])

	const isAutoMode = chicken.betMode === 'auto'
	const isLive = chicken.phase === 'live'
	const busy = chicken.isSubmitting || chicken.isAnimating
	const controlsDisabled = isLive || busy || chicken.autoBetRunning
	const previewCurrent = selectChickenStepMultiplier(chicken.multiplierSteps, chicken.step)
	const previewNext = selectChickenStepMultiplier(chicken.multiplierSteps, chicken.step + 1)
	const currentMultiplier = chicken.currentMultiplier > 0 ? chicken.currentMultiplier : previewCurrent
	const nextMultiplier = previewNext
	const totalRoads = chicken.multiplierSteps.length
	const atFinish = isLive && totalRoads > 0 && chicken.step >= totalRoads
	const autoCashoutMultiplier = selectChickenStepMultiplier(chicken.multiplierSteps, chicken.autoBetSettings.steps)

	useEffect(() => {
		setAmountDraft(chicken.betAmount)
	}, [chicken.betAmount])

	useEffect(() => {
		setAutoBetCountDraft(String(chicken.autoBetSettings.betCount))
	}, [chicken.autoBetSettings.betCount])

	useEffect(() => {
		setCashoutStepsDraft(String(chicken.autoBetSettings.steps))
	}, [chicken.autoBetSettings.steps])

	const commitAmount = useCallback(() => {
		const parsed = Number(amountDraft)
		if (!Number.isFinite(parsed) || parsed <= 0) {
			setAmountDraft(chicken.betAmount)
			return
		}

		dispatch(setChickenBetAmount(parsed.toString()))
	}, [amountDraft, dispatch, chicken.betAmount])

	const handleQuickAmount = useCallback(
		(multiplier: number) => {
			const parsed = Number(amountDraft) || Number(chicken.betAmount) || 0
			const next = Math.max(0, parsed * multiplier)
			const nextAmount = next > 0 ? String(next) : '0'

			setAmountDraft(nextAmount)
			dispatch(setChickenBetAmount(nextAmount))
		},
		[amountDraft, dispatch, chicken.betAmount],
	)

	const handleModeChange = useCallback(
		(value: string) => {
			if (chicken.autoBetRunning || chicken.phase === 'live') {
				return
			}

			dispatch(setChickenBetMode(value as ChickenBetMode))
		},
		[chicken.autoBetRunning, chicken.phase, dispatch],
	)

	const primaryAction = useMemo(() => {
		if (!isAuthenticated) {
			return { label: 'Bet', variant: 'secondary' as const, disabled: true, onClick: () => undefined }
		}

		if (isAutoMode) {
			if (chicken.autoBetRunning) {
				return {
					label: 'Stop Autobet',
					variant: 'destructive' as const,
					disabled: false,
					onClick: () => controller?.stopAutoBet(),
				}
			}

			return {
				label: 'Start Autobet',
				variant: 'default' as const,
				disabled: busy || chicken.autoBetSettings.steps < 1 || totalRoads < 1,
				onClick: () => void controller?.startAutoBet(),
			}
		}

		if (isLive) {
			return {
				label: nextMultiplier > 0 ? `Go ${nextMultiplier.toFixed(2)}x` : 'Go',
				variant: 'default' as const,
				disabled: busy || atFinish || nextMultiplier <= 0,
				onClick: () => void controller?.stepForward(),
			}
		}

		return {
			label: 'Bet',
			variant: 'default' as const,
			disabled: busy,
			onClick: () => void controller?.startGame(),
		}
	}, [atFinish, busy, chicken.autoBetRunning, chicken.autoBetSettings.steps, controller, isAuthenticated, isAutoMode, isLive, nextMultiplier, totalRoads])

	return (
		<div className={cn('flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-card p-4', className)}>
			<Tabs value={chicken.betMode} onValueChange={handleModeChange} className='shrink-0'>
				<TabsList className='grid h-11 w-full grid-cols-2 rounded-xl bg-secondary p-1'>
					<TabsTrigger value='manual' disabled={chicken.autoBetRunning || isLive} className='rounded-lg data-[state=active]:bg-background'>
						Manual
					</TabsTrigger>
					<TabsTrigger value='auto' disabled={chicken.autoBetRunning || isLive} className='rounded-lg data-[state=active]:bg-background'>
						Auto
					</TabsTrigger>
				</TabsList>
			</Tabs>

			<div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto'>
				<div className={cn('space-y-2', controlsDisabled && 'pointer-events-none opacity-50')}>
				<div className='flex items-center justify-between gap-3'>
					<label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Bet Amount</label>
					<span className='text-xs font-medium text-muted-foreground'>${formatBalance(usdAmount, 2)}</span>
				</div>
				<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
					<div className='relative min-w-0 flex-1'>
						<input
							type='text'
							inputMode='decimal'
							value={amountDraft}
							onChange={(event) => setAmountDraft(event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1'))}
							onBlur={commitAmount}
							className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none sm:text-base sm:tracking-[-0.24px]'
						/>
						<CurrencyIcon
							code={chicken.currencyCode}
							symbolUrl={currency?.symbol_url}
							size='sm'
							className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'
						/>
					</div>
					<div className='flex w-[88px] shrink-0 border-l border-border'>
						<button type='button' onClick={() => handleQuickAmount(0.5)} className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent'>
							½
						</button>
						<span className='w-px bg-border' aria-hidden />
						<button type='button' onClick={() => handleQuickAmount(2)} className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent'>
							2×
						</button>
					</div>
				</div>
			</div>

			{!isLive || isAutoMode ? (
				<div className={cn('space-y-2', controlsDisabled && 'pointer-events-none opacity-50')}>
					<label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Difficulty</label>
					<Select value={chicken.difficulty} onValueChange={(value) => dispatch(setChickenDifficulty(value as ChickenDifficulty))} disabled={controlsDisabled}>
						<SelectTrigger className='h-11 rounded-xl border-border bg-secondary'>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{CHICKEN_DIFFICULTIES.map((difficulty) => (
								<SelectItem key={difficulty} value={difficulty}>
									{DIFFICULTY_LABELS[difficulty]}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			) : (
				<div className='grid grid-cols-2 gap-3 rounded-xl border border-border bg-secondary/60 px-4 py-3'>
					<div>
						<p className='text-xs uppercase tracking-wide text-muted-foreground'>Difficulty</p>
						<p className='mt-1 text-lg font-semibold capitalize text-foreground'>{chicken.difficulty}</p>
					</div>
					<div>
						<p className='text-xs uppercase tracking-wide text-muted-foreground'>Multiplier</p>
						<p className='mt-1 text-lg font-semibold text-foreground'>
							{chicken.step > 0 && currentMultiplier > 0 ? `${currentMultiplier.toFixed(2)}x` : nextMultiplier > 0 ? `${nextMultiplier.toFixed(2)}x` : '—'}
						</p>
					</div>
				</div>
			)}

			{isLive && !isAutoMode ? (
				<div className='space-y-2'>
					<div className='flex items-center justify-between'>
						<label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Total Profit</label>
						<span className='text-xs font-medium text-muted-foreground'>{currentMultiplier > 0 ? `${currentMultiplier.toFixed(2)}x` : '—'}</span>
					</div>
					<input
						value={formatBalance(chicken.step > 0 ? (chicken.currentPayout > 0 ? chicken.currentPayout : Number(chicken.betAmount) * currentMultiplier) : 0, 4)}
						readOnly
						className='flex h-11 w-full rounded-xl border border-border bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none sm:text-base sm:tracking-[-0.24px]'
					/>
					<Button type='button' variant='outline' disabled={busy || chicken.step <= 0 || !controller} onClick={() => void controller?.cashOut()} className='h-11 w-full'>
						Cash Out {chicken.step > 0 && currentMultiplier > 0 ? `${currentMultiplier.toFixed(2)}x` : ''}
					</Button>
				</div>
			) : null}

			{isAutoMode ? (
				<div className={cn('space-y-3', chicken.autoBetRunning && 'pointer-events-none opacity-50')}>
					<div className='space-y-2'>
						<div className='flex items-center justify-between'>
							<label className='text-xs text-muted-foreground'>Cashout Steps</label>
							<span className='text-xs font-medium text-muted-foreground'>{autoCashoutMultiplier > 0 ? `${autoCashoutMultiplier.toFixed(2)}x` : '—'}</span>
						</div>
						<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
							<input
								type='text'
								inputMode='numeric'
								value={cashoutStepsDraft}
								disabled={chicken.autoBetRunning || totalRoads < 1}
								onChange={(event) => {
									const next = event.target.value.replace(/[^\d]/g, '')
									setCashoutStepsDraft(next)
									const parsed = Number(next)
									if (!Number.isInteger(parsed) || parsed < 1) {
										return
									}

									dispatch(setChickenAutoBetSettings({ steps: parsed }))
								}}
								onBlur={() => {
									const max = Math.max(1, totalRoads)
									const parsed = Number(cashoutStepsDraft)
									const steps = Number.isInteger(parsed) ? Math.min(max, Math.max(1, parsed)) : chicken.autoBetSettings.steps
									setCashoutStepsDraft(String(steps))
									dispatch(setChickenAutoBetSettings({ steps }))
								}}
								className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none disabled:opacity-50 sm:text-base sm:tracking-[-0.24px]'
							/>
						</div>
					</div>
					<div className='space-y-2'>
						<label className='text-xs text-muted-foreground'>Number of Bets (0 = ∞)</label>
						<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
							<input
								type='text'
								inputMode='numeric'
								value={autoBetCountDraft}
								disabled={chicken.autoBetRunning}
								onChange={(event) => {
									const next = event.target.value.replace(/[^\d]/g, '')
									setAutoBetCountDraft(next)
									const parsed = Number(next)
									dispatch(
										setChickenAutoBetSettings({
											betCount: Number.isFinite(parsed) ? Math.max(0, parsed) : 0,
											isInfinite: parsed === 0,
										}),
									)
								}}
								className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none disabled:opacity-50 sm:text-base sm:tracking-[-0.24px]'
							/>
						</div>
					</div>
					<div className='space-y-2'>
						<label className='text-xs text-muted-foreground'>Stop on Profit</label>
						<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
							<input
								type='text'
								inputMode='decimal'
								value={stopOnProfitDraft}
								disabled={chicken.autoBetRunning}
								onChange={(event) => {
									const next = event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
									setStopOnProfitDraft(next)
									const parsed = Number(next)
									if (Number.isFinite(parsed)) {
										dispatch(setChickenAutoBetSettings({ stopOnProfit: Math.max(0, parsed) }))
									}
								}}
								className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none disabled:opacity-50 sm:text-base sm:tracking-[-0.24px]'
							/>
						</div>
					</div>
					<div className='space-y-2'>
						<label className='text-xs text-muted-foreground'>Stop on Loss</label>
						<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
							<input
								type='text'
								inputMode='decimal'
								value={stopOnLossDraft}
								disabled={chicken.autoBetRunning}
								onChange={(event) => {
									const next = event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
									setStopOnLossDraft(next)
									const parsed = Number(next)
									if (Number.isFinite(parsed)) {
										dispatch(setChickenAutoBetSettings({ stopOnLoss: Math.max(0, parsed) }))
									}
								}}
								className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none disabled:opacity-50 sm:text-base sm:tracking-[-0.24px]'
							/>
						</div>
					</div>
				</div>
			) : null}
			</div>

			<div className='shrink-0'>
				<Button
					type='button'
					variant={primaryAction.variant}
					disabled={primaryAction.disabled || !controller}
					onClick={primaryAction.onClick}
					className='h-12 w-full text-base font-semibold'
				>
					{primaryAction.label}
				</Button>
			</div>
		</div>
	)
}
